import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';

const SupervisorSelect = ({
  value,
  onChange,
  employees = [],
  placeholder = 'Select Supervisor',
  error,
  excludeEmployeeId = null,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Sort employees A-Z, filter out excluded employee, and ensure unique entries
  const sortedEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];
    
    // Deduplicate employees by unique employee ID or code
    const uniqueMap = new Map();
    employees.forEach(emp => {
      if (!emp) return;
      const uniqueKey = String(emp.Id || emp.EmployeeId || emp.EmployeeCode || emp.employeeCode || '');
      if (uniqueKey && !uniqueMap.has(uniqueKey)) {
        uniqueMap.set(uniqueKey, emp);
      }
    });

    return Array.from(uniqueMap.values())
      .filter(emp => {
        if (!emp) return false;
        if (excludeEmployeeId && String(emp.Id || emp.EmployeeId) === String(excludeEmployeeId)) {
          return false;
        }
        return true;
      })
      .map(emp => ({
        id: emp.Id || emp.EmployeeId || '',
        name: emp.Name || emp.name || `${emp.FirstName || ''} ${emp.LastName || ''}`.trim() || 'Unknown',
        code: emp.EmployeeCode || emp.employeeCode || emp.EmployeeId || '',
        email: emp.Email || emp.email || ''
      }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [employees, excludeEmployeeId]);

  // Filtered by search query
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return sortedEmployees;
    const q = searchQuery.toLowerCase().trim();
    return sortedEmployees.filter(emp =>
      emp.name.toLowerCase().includes(q) ||
      emp.code.toLowerCase().includes(q) ||
      emp.email.toLowerCase().includes(q)
    );
  }, [sortedEmployees, searchQuery]);

  // Find currently selected employee
  const selectedEmployee = useMemo(() => {
    if (!value) return null;
    return sortedEmployees.find(emp => String(emp.id) === String(value)) || null;
  }, [value, sortedEmployees]);

  const handleSelect = (empId) => {
    onChange(empId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchQuery('');
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        userSelect: 'none'
      }}
    >
      {/* Trigger Control */}
      <div
        onClick={() => {
          if (!disabled) setIsOpen(prev => !prev);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.65rem 0.85rem',
          borderRadius: '0.5rem',
          border: error ? '1px solid #ef4444' : isOpen ? '1px solid #3b5bfd' : '1px solid #d1d5db',
          backgroundColor: disabled ? '#f3f4f6' : '#ffffff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          minHeight: '42px',
          boxShadow: isOpen ? '0 0 0 2px rgba(15, 118, 110, 0.15)' : 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s'
        }}
      >
        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedEmployee ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}>
                {selectedEmployee.name}
              </span>
              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                ({selectedEmployee.code})
              </span>
            </div>
          ) : (
            <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              {placeholder}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px' }}>
          {selectedEmployee && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              title="Clear selection"
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: '2px',
                color: '#9ca3af',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px'
              }}
              onMouseOver={(e) => e.currentTarget.style.color = '#ef4444'}
              onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={16}
            style={{
              color: '#6b7280',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          />
        </div>
      </div>

      {/* Floating Dropdown Panel */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 1100,
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden'
          }}
        >
          {/* Search Header */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', position: 'relative' }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: '18px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8'
              }}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, code, email..."
              style={{
                width: '100%',
                padding: '6px 10px 6px 30px',
                fontSize: '0.82rem',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                outline: 'none',
                backgroundColor: '#f8fafc',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b5bfd'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {/* Options List */}
          <div
            style={{
              maxHeight: '260px',
              overflowY: 'auto',
              scrollbarWidth: 'thin'
            }}
          >
            {/* Clear / None option */}
            <div
              onClick={() => handleSelect('')}
              style={{
                padding: '8px 14px',
                fontSize: '0.82rem',
                color: '#64748b',
                fontStyle: 'italic',
                cursor: 'pointer',
                borderBottom: '1px solid #f1f5f9',
                backgroundColor: !value ? '#f0fdf4' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = !value ? '#f0fdf4' : 'transparent'}
            >
              <span>-- None / No Supervisor --</span>
              {!value && <Check size={14} style={{ color: '#3b5bfd' }} />}
            </div>

            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((emp) => {
                const isSelected = String(emp.id) === String(value);
                return (
                  <div
                    key={emp.id}
                    onClick={() => handleSelect(emp.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#ecfdf5' : 'transparent',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseOver={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseOut={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Left: Employee Name */}
                    <div style={{ flex: 1, minWidth: 0, paddingRight: '10px' }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: '0.88rem',
                          color: isSelected ? '#3b5bfd' : '#0f172a',
                          lineHeight: '1.25'
                        }}
                      >
                        {emp.name}
                      </div>
                    </div>

                    {/* Right: Code · Email */}
                    <div
                      style={{
                        flexShrink: 0,
                        maxWidth: '58%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.78rem',
                        color: '#64748b'
                      }}
                    >
                      <span style={{ fontWeight: 500, color: '#475569' }}>{emp.code}</span>
                      {emp.email && (
                        <>
                          <span style={{ color: '#cbd5e1' }}>·</span>
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '150px'
                            }}
                            title={emp.email}
                          >
                            {emp.email}
                          </span>
                        </>
                      )}
                      {isSelected && (
                        <Check size={14} style={{ color: '#3b5bfd', marginLeft: '4px', flexShrink: 0 }} />
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>
                No matching supervisors found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(SupervisorSelect);
